import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';

/*
 * The INTERNAL note on one product line of a deal, quote or invoice, and the
 * tracker's "Notes" cell (owner 2026-10-18). Never sent to Xero, never on a
 * PDF; separate from the customer-facing `description`.
 *
 * Rendered in a slim sub-row under each product row, so it stays text-xs and
 * pulls its control margins in rather than adding a full control height.
 *
 * - `value === undefined` is an older API that does not report notes: render
 *   nothing, never an "Add note" that would write a key the server refuses.
 * - Empty → a ghost "Add note" button. Filled → the note in full; click edits.
 * - Saves on blur and Ctrl/Cmd+Enter; Escape cancels (and is stopped so a
 *   host Modal does not close). A commit equal to the stored value is a state
 *   reset, never a request. `''` / whitespace-only saves as `null`.
 * - `onSave` THROWS on failure: the editor stays open with the draft and the
 *   message, so nothing typed is lost.
 * - `disabled`: a filled note is shown read-only; an empty one renders nothing.
 *
 * The caller owns the request (the line PATCH with `{ notes }` only, or
 * `api.setDeliverableLineNote`) and updating its row afterwards.
 */

export const LINE_NOTE_MAX_LENGTH = 2000;

export interface LineNoteFieldProps {
  value: string | null | undefined;
  onSave(next: string | null): Promise<void>;
  disabled?: boolean;
  className?: string;
}

function normalise(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

function failureMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = error.fieldErrors;
    return fields.notes ?? fields.note ?? error.formMessage ?? error.message;
  }
  return 'The note could not be saved.';
}

function LineNoteIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5Z" />
      <path d="M15 3v5a1 1 0 0 0 1 1h5" />
    </svg>
  );
}

export function LineNoteField({ value, onSave, disabled = false, className }: LineNoteFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * What the last successful save wrote, shown until the caller's `value`
   * catches up — so a parent that reloads asynchronously does not flash the
   * old note. Keyed by the `value` it was saved over, so it lapses by itself
   * the moment `value` changes.
   */
  const [savedOverride, setSavedOverride] = useState<{
    over: string | null | undefined;
    next: string | null;
  } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const wantsTriggerFocus = useRef(false);
  // Set on Escape so a blur fired as the editor unmounts cannot save the draft.
  const cancelled = useRef(false);

  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [editing]);

  useEffect(() => {
    if (editing || !wantsTriggerFocus.current) return;
    wantsTriggerFocus.current = false;
    triggerRef.current?.focus();
  }, [editing]);

  if (value === undefined) return null;

  const shown =
    savedOverride !== null && savedOverride.over === value ? savedOverride.next : value;

  function begin() {
    if (disabled) return;
    setDraft(shown ?? '');
    setError(null);
    cancelled.current = false;
    setEditing(true);
  }

  function close(returnFocus: boolean) {
    wantsTriggerFocus.current = returnFocus;
    setEditing(false);
    setError(null);
  }

  async function commit(returnFocus: boolean) {
    if (saving || cancelled.current) return;
    const next = normalise(draft);
    if (next === (shown ?? null)) {
      close(returnFocus);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setSavedOverride({ over: value, next });
      setSaving(false);
      close(returnFocus);
    } catch (caught) {
      setSaving(false);
      setError(failureMessage(caught));
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      // Stop it here so a host Modal does not close with the draft in it.
      event.preventDefault();
      event.stopPropagation();
      // A request in flight cannot be recalled, so Escape does not pretend to.
      if (!saving) {
        cancelled.current = true;
        close(true);
      }
      return;
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void commit(true);
    }
  }

  if (editing) {
    return (
      <div className={cx('grid min-w-0 gap-1', className)}>
        <Textarea
          ref={textareaRef}
          aria-label="Note"
          value={draft}
          maxLength={LINE_NOTE_MAX_LENGTH}
          autoGrow={{ minRows: 1, maxRows: 6 }}
          readOnly={saving}
          aria-busy={saving || undefined}
          invalid={error !== null}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            // A failed save keeps the editor open; blurring again retries.
            void commit(false);
          }}
          className="px-2 py-1 text-xs"
        />
        {saving ? <p className="text-xs text-fg-muted">Saving…</p> : null}
        {error ? (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  if (shown === null) {
    if (disabled) return null;
    return (
      <div className={cx('min-w-0', className)}>
        <Button
          ref={triggerRef}
          variant="ghost"
          size="sm"
          leadingIcon={<LineNoteIcon />}
          onClick={begin}
          className="-my-1.5 -ml-2.5 text-fg-subtle"
        >
          Add note
        </Button>
      </div>
    );
  }

  if (disabled) {
    return (
      <div
        className={cx(
          'flex min-w-0 items-start gap-1.5 text-xs text-fg-muted',
          className,
        )}
      >
        <LineNoteIcon />
        <span className="sr-only">Note: </span>
        <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{shown}</span>
      </div>
    );
  }

  return (
    <div className={cx('min-w-0', className)}>
      <button
        type="button"
        ref={triggerRef}
        onClick={begin}
        aria-label={`Note: ${shown}`}
        className={cx(
          '-mx-1.5 -my-1 flex w-full min-w-0 items-start gap-1.5 rounded-md px-1.5 py-1 text-left text-xs text-fg-muted',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:bg-surface-hover hover:text-fg',
        )}
      >
        <span className="mt-px">
          <LineNoteIcon />
        </span>
        <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{shown}</span>
      </button>
    </div>
  );
}
